/**
 * iter170 — Native push registration for the Capacitor mobile shell.
 *
 * This module is import-safe on the web: every Capacitor API is gated on
 * `Capacitor.isNativePlatform()`, so the browser bundle simply no-ops.
 *
 * Flow (only fires inside the .ipa / .apk):
 *   1. Request permission via the OS prompt.
 *   2. If granted, call PushNotifications.register() which kicks off APNs
 *      registration on iOS / FCM on Android.
 *   3. The 'registration' event fires with an FCM token (yes, FCM — the
 *      Capacitor plugin proxies APNs → FCM on iOS so we treat both
 *      platforms identically downstream).
 *   4. POST {token, platform} to the EXISTING /api/push/fcm/register
 *      endpoint — same path the web/PWA already uses via FcmSection.
 *   5. Wire up two listeners:
 *        - pushNotificationReceived  → toast inside the app (foreground)
 *        - pushNotificationActionPerformed → navigate to data.route (tap)
 *
 * Backend ↔ frontend handshake:
 *   When sending an FCM message that should deep-link inside the app, set
 *   data: { route: "/chat" } (or "/labs", "/u/neo", …). The native shell
 *   will call the React-Router navigate() callback supplied by the caller.
 */

import { api } from "./api";

// Single-shot guard so a tab-switch doesn't try to re-register listeners.
let _initialized = false;

async function _loadCapacitor() {
    // Dynamic import keeps the web bundle from eagerly pulling in the
    // native plugin shim. Errors here mean Capacitor isn't installed (impossible
    // in a properly built shell), so we silently bail.
    try {
        const { Capacitor } = await import("@capacitor/core");
        return { Capacitor };
    } catch {
        return null;
    }
}

export async function isNativeShell() {
    const mod = await _loadCapacitor();
    return !!(mod && mod.Capacitor.isNativePlatform && mod.Capacitor.isNativePlatform());
}

/**
 * Initialise native push for the logged-in user. Safe to call multiple times.
 * @param {{ navigate?: (path: string) => void, onForeground?: (n: any) => void }} opts
 */
export async function initNativePush(opts = {}) {
    if (_initialized) return { initialized: true };

    const mod = await _loadCapacitor();
    if (!mod || !mod.Capacitor.isNativePlatform()) {
        // Web / PWA path — the existing FcmSection component handles browser
        // FCM registration via the Firebase JS SDK. Nothing to do here.
        return { initialized: false, reason: "web" };
    }

    let PushNotifications;
    try {
        ({ PushNotifications } = await import("@capacitor/push-notifications"));
    } catch {
        return { initialized: false, reason: "plugin-missing" };
    }

    // 1. Permission gate
    let perm;
    try {
        perm = await PushNotifications.checkPermissions();
        if (perm.receive !== "granted") {
            perm = await PushNotifications.requestPermissions();
        }
    } catch (e) {
        return { initialized: false, reason: "perm-error", error: String(e) };
    }
    if (perm.receive !== "granted") {
        return { initialized: false, reason: "permission-denied" };
    }

    // 2. Wire listeners BEFORE register() — registration event can fire
    //    synchronously on warm starts.
    const platform = mod.Capacitor.getPlatform(); // 'ios' | 'android'

    PushNotifications.addListener("registration", async (token) => {
        const value = token && token.value;
        if (!value || value.length < 32) return;
        try {
            await api.post("/push/fcm/register", {
                token: value,
                platform,
                user_agent: `Capacitor/${platform}`,
            });
        } catch {
            // Non-fatal — we'll retry on next launch via the same flow.
        }
    });

    PushNotifications.addListener("registrationError", () => {
        // Swallow — typically means APNs/FCM hasn't been configured yet in
        // the Firebase project. User-visible toast would be noise.
    });

    PushNotifications.addListener("pushNotificationReceived", (notif) => {
        // Foreground delivery — let the host app decide what to do (banner,
        // toast, badge update, etc.) via the optional callback.
        if (typeof opts.onForeground === "function") {
            try { opts.onForeground(notif); } catch { /* */ }
        }
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
        // User tapped the system notification — open the deep link.
        const data = (event && event.notification && event.notification.data) || {};
        const route = data.route || data.path || data.deep_link;
        if (route && typeof opts.navigate === "function") {
            try { opts.navigate(String(route)); } catch { /* */ }
        }
    });

    // 3. Kick off registration. This triggers the 'registration' listener
    //    above (or 'registrationError') asynchronously.
    try {
        await PushNotifications.register();
    } catch (e) {
        return { initialized: false, reason: "register-failed", error: String(e) };
    }

    _initialized = true;
    return { initialized: true, platform };
}

/**
 * Best-effort token unregister. Called from /logout.
 */
export async function disposeNativePush() {
    if (!_initialized) return;
    try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        await PushNotifications.removeAllListeners();
    } catch { /* */ }
    _initialized = false;
}
